/** Back office → Academy → Students (D-120): add a student and open the exams they want (TCF, TEF,
 * IELTS, German); the student receives an e-mail with everything to sign in (one-time code, no
 * password). Then the record: change the exams, extend, suspend, resume, withdraw the access (nothing
 * is deleted), resend the access e-mail, and the history of who did what. CSV import of a list. */

import {
  ApiError,
  type ExamEnum,
  type StudentCreateRequest,
  type StudentDetail,
  type StudentImportRow,
  useAcademyStudentsCreate,
  useAcademyStudentsExamsCreate,
  useAcademyStudentsExtendCreate,
  useAcademyStudentsHistoryList,
  useAcademyStudentsImportCreate,
  useAcademyStudentsList,
  useAcademyStudentsOptionsRetrieve,
  useAcademyStudentsResendCreate,
  useAcademyStudentsResumeCreate,
  useAcademyStudentsRetrieve,
  useAcademyStudentsRevokeCreate,
  useAcademyStudentsSuspendCreate,
} from "@agencyos/api-client";
import { countryName, countryOptions } from "@agencyos/core";
import { Badge, Button, Checkbox, Dialog, GlassCard, SegmentedControl, SelectField, Skeleton, TextField } from "@agencyos/ui-web";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { MotherTongueSelect } from "../../academy/ielts/MotherTongue";
import { fieldErrors } from "../../lib/settings";
import { dateText } from "../billing/shared";
import { ErrorList, LEVELS, Pager, useRefreshAcademy } from "./shared";
import { OralCreditsAdmin } from "./OralCreditsAdmin";

const EXAMS: ExamEnum[] = ["tcf_canada", "tef_canada", "ielts_general", "goethe"];
const KINDS = ["gift", "paid", "trial"] as const;
type Kind = (typeof KINDS)[number];
type ExamChoice = { on: boolean; target: string; level: string };
const STATUS_TONE: Record<string, "success" | "default" | "warning" | "danger"> = {
  active: "success",
  pending: "warning",
  suspended: "warning",
  revoked: "danger",
  refunded: "danger",
  expired: "default",
};

/** The goal field of an exam: NCLC for the TCF and the TEF, band for the IELTS, points for German. */
function targetHint(exam: ExamEnum): { min: number; max: number; step: number } {
  if (exam === "ielts_general") return { min: 0, max: 9, step: 0.5 };
  if (exam === "goethe") return { min: 60, max: 100, step: 1 };
  return { min: 1, max: 12, step: 1 };
}

function blankChoices(): Record<ExamEnum, ExamChoice> {
  return Object.fromEntries(EXAMS.map((e) => [e, { on: false, target: "", level: "B1" }])) as Record<ExamEnum, ExamChoice>;
}

function examsPayload(choices: Record<ExamEnum, ExamChoice>) {
  return EXAMS.filter((e) => choices[e].on).map((exam) => ({
    exam,
    ...(choices[exam].target ? { target: Number(choices[exam].target) } : {}),
    ...(exam === "goethe" ? { level: choices[exam].level as (typeof LEVELS)[number] } : {}),
  }));
}

function ExamPicker({ value, onChange, offered }: { value: Record<ExamEnum, ExamChoice>; onChange: (next: Record<ExamEnum, ExamChoice>) => void; offered: readonly string[] }) {
  const { t } = useTranslation();
  const set = (exam: ExamEnum, patch: Partial<ExamChoice>) => onChange({ ...value, [exam]: { ...value[exam], ...patch } });
  return (
    <fieldset className="stack students__exams">
      <legend>{t("access.students.exams")}</legend>
      {EXAMS.filter((e) => offered.includes(e)).map((exam) => {
        const hint = targetHint(exam);
        return (
          <div key={exam} className="row wrap students__exam">
            <Checkbox label={t(`access.exam.${exam}`)} checked={value[exam].on} onChange={(e) => set(exam, { on: e.target.checked })} />
            {value[exam].on ? (
              <>
                {exam === "goethe" ? (
                  <SelectField
                    label={t("access.students.level")}
                    value={value[exam].level}
                    options={LEVELS.map((l) => ({ value: l, label: l }))}
                    onChange={(e) => set(exam, { level: e.target.value })}
                  />
                ) : null}
                <TextField
                  label={t(`access.students.target.${exam}`)}
                  type="number"
                  inputMode="decimal"
                  min={hint.min}
                  max={hint.max}
                  step={hint.step}
                  value={value[exam].target}
                  onChange={(e) => set(exam, { target: e.target.value })}
                />
              </>
            ) : null}
          </div>
        );
      })}
    </fieldset>
  );
}

function AddStudent({ onDone, onOpen }: { onDone: (id: string) => void; onOpen: (id: string) => void }) {
  const { t, i18n } = useTranslation();
  const refresh = useRefreshAcademy();
  const options = useAcademyStudentsOptionsRetrieve();
  const opts = options.data?.status === 200 ? options.data.data : undefined;
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState("CM");
  const [language, setLanguage] = useState(i18n.language === "en" ? "en" : "fr");
  const [tongue, setTongue] = useState("");
  const [choices, setChoices] = useState(blankChoices);
  const [kind, setKind] = useState<Kind>("gift");
  const [days, setDays] = useState("");
  const [endsOn, setEndsOn] = useState("");
  const [reason, setReason] = useState("");
  const [method, setMethod] = useState("");
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const create = useAcademyStudentsCreate({ mutation: { onSuccess: refresh } });
  const errors = fieldErrors(create.error);
  const existing = create.error instanceof ApiError ? ((create.error.body as Record<string, unknown> | null)?.learner_id as string | undefined) : undefined;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const data: StudentCreateRequest = {
      full_name: fullName,
      email,
      phone,
      country,
      interface_language: language as StudentCreateRequest["interface_language"],
      mother_tongue: tongue,
      exams: examsPayload(choices),
      access: {
        kind,
        ...(days ? { days: Number(days) } : {}),
        ...(endsOn ? { ends_on: endsOn } : {}),
        reason,
      },
      ...(kind === "paid"
        ? { payment: { method: method as NonNullable<StudentCreateRequest["payment"]>["method"], ...(amount ? { amount_xaf: Number(amount) } : {}), reference, phone } }
        : {}),
    };
    create.mutate({ data }, { onSuccess: (response) => response.status === 201 && onDone(response.data.id) });
  };

  return (
    <GlassCard as="section" className="stack">
      <h2>{t("access.students.add")}</h2>
      <p className="muted">{t("access.students.addIntro")}</p>
      <form className="stack" onSubmit={onSubmit} noValidate>
        <div className="grid-2">
          <TextField label={t("access.students.fullName")} value={fullName} error={errors.full_name} onChange={(e) => setFullName(e.target.value)} required />
          <TextField label={t("access.students.email")} type="email" value={email} error={errors.email} onChange={(e) => setEmail(e.target.value)} required />
          <TextField label={t("access.students.phone")} hint={t("access.students.phoneHint")} type="tel" value={phone} error={errors.phone} onChange={(e) => setPhone(e.target.value)} />
          <SelectField
            label={t("access.students.country")}
            value={country}
            error={errors.country}
            options={countryOptions(i18n.language)}
            onChange={(e) => setCountry(e.target.value)}
          />
          <SelectField
            label={t("access.students.language")}
            value={language}
            options={[
              { value: "fr", label: "Français" },
              { value: "en", label: "English" },
            ]}
            onChange={(e) => setLanguage(e.target.value)}
          />
          <MotherTongueSelect value={tongue} onChange={setTongue} />
        </div>
        <ExamPicker value={choices} onChange={setChoices} offered={opts?.exams ?? EXAMS} />
        {errors.exams ? <p role="alert" className="ko">{errors.exams}</p> : null}
        <SegmentedControl
          label={t("access.students.kind")}
          value={kind}
          onChange={(value) => setKind(value as Kind)}
          options={KINDS.map((k) => ({ value: k, label: t(`access.kind.${k}`) }))}
        />
        <p className="muted">{t(`access.kindHelp.${kind}`)}</p>
        <div className="grid-2">
          <TextField
            label={t("access.students.days")}
            hint={t("access.students.daysHint", { days: opts?.default_days ?? 30 })}
            type="number"
            min={1}
            max={opts?.max_days}
            value={days}
            error={errors.days}
            onChange={(e) => setDays(e.target.value)}
          />
          <TextField label={t("access.students.endsOn")} type="date" value={endsOn} error={errors.ends_on} onChange={(e) => setEndsOn(e.target.value)} />
        </div>
        {kind === "gift" ? (
          <TextField label={t("access.students.reason")} hint={t("access.students.reasonHint")} value={reason} error={errors.reason} onChange={(e) => setReason(e.target.value)} />
        ) : null}
        {kind === "paid" ? (
          <fieldset className="stack">
            <legend>{t("access.students.payment")}</legend>
            <SelectField
              label={t("access.students.method")}
              value={method}
              options={[{ value: "", label: t("access.method.later") }, ...(opts?.payment_methods ?? []).map((m) => ({ value: m, label: t(`access.method.${m}`) }))]}
              onChange={(e) => setMethod(e.target.value)}
            />
            <TextField label={t("access.students.amount")} hint={t("access.students.amountHint")} type="number" min={1} value={amount} error={errors.amount_xaf} onChange={(e) => setAmount(e.target.value)} />
            {method === "transfer" || method === "deposit" ? (
              <TextField label={t("access.students.reference")} value={reference} error={errors.reference} onChange={(e) => setReference(e.target.value)} />
            ) : null}
            <p className="muted">{t("access.students.paymentHint")}</p>
          </fieldset>
        ) : null}
        {create.isError && !errors.email ? <ErrorList error={create.error} /> : null}
        {existing ? (
          <Button type="button" variant="secondary" onClick={() => onOpen(existing)}>
            {t("access.students.openExisting")}
          </Button>
        ) : null}
        <div className="row">
          <Button type="submit" disabled={create.isPending || !email.trim() || !fullName.trim()}>
            {t("access.students.create")}
          </Button>
        </div>
        <p className="muted">{t("access.students.emailNote")}</p>
      </form>
    </GlassCard>
  );
}

type Pending = "extend" | "suspend" | "revoke" | null;

function StudentRecord({ id, canManage, canResend, onClose }: { id: string; canManage: boolean; canResend: boolean; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const refresh = useRefreshAcademy();
  const record = useAcademyStudentsRetrieve(id);
  const history = useAcademyStudentsHistoryList(id);
  const done = { mutation: { onSuccess: refresh } };
  const changeExams = useAcademyStudentsExamsCreate(done);
  const extend = useAcademyStudentsExtendCreate(done);
  const suspend = useAcademyStudentsSuspendCreate(done);
  const resume = useAcademyStudentsResumeCreate(done);
  const revoke = useAcademyStudentsRevokeCreate(done);
  const resend = useAcademyStudentsResendCreate(done);
  const [editing, setEditing] = useState(false);
  const [choices, setChoices] = useState(blankChoices);
  const [pending, setPending] = useState<Pending>(null);
  const [note, setNote] = useState("");
  const [days, setDays] = useState("30");
  const student: StudentDetail | undefined = record.data?.status === 200 ? record.data.data : undefined;
  const rows = history.data?.status === 200 ? history.data.data : [];
  if (!student) return <Skeleton style={{ height: 240 }} />;
  const current = student.current;
  const error = changeExams.error ?? extend.error ?? suspend.error ?? resume.error ?? revoke.error ?? resend.error;

  const startEditing = () => {
    const next = blankChoices();
    // D-160: the exams of the student space when the agency or the sign-up set them
    const chosen: readonly string[] = student.chosen_exams ?? [];
    for (const exam of EXAMS) next[exam].on = chosen.length ? chosen.includes(exam) : Boolean(student.access[exam as keyof typeof student.access]);
    setChoices(next);
    setEditing(true);
  };
  const confirm = () => {
    if (pending === "extend") extend.mutate({ id, data: { days: Number(days), note } });
    if (pending === "suspend") suspend.mutate({ id, data: { note } });
    if (pending === "revoke") revoke.mutate({ id, data: { note } });
    setPending(null);
    setNote("");
  };

  return (
    <GlassCard as="section" className="stack" aria-labelledby={`student-${id}`}>
      <div className="section-title">
        <div>
          <h2 id={`student-${id}`}>{student.full_name || student.email}</h2>
          <p className="muted">
            {student.email}
            {student.phone ? ` · ${student.phone}` : ""}
            {student.country ? ` · ${countryName(student.country, i18n.language)}` : ""}
          </p>
        </div>
        <Button variant="ghost" onClick={onClose}>
          {t("access.students.back")}
        </Button>
      </div>
      <div className="row wrap">
        {EXAMS.map((exam) =>
          student.access[exam as keyof typeof student.access] ? (
            <Badge key={exam} tone="success">
              {t(`access.exam.${exam}`)}
            </Badge>
          ) : null,
        )}
        {!Object.values(student.access).some(Boolean) ? <Badge>{t("access.students.noAccess")}</Badge> : null}
      </div>
      <p className="muted">
        {t("access.students.space")}{" "}
        {student.chosen_exams?.length ? student.space_exams.map((exam) => t(`access.exam.${exam}`)).join(", ") : t("access.students.spaceAll")}
      </p>
      {current ? (
        <p>
          <Badge tone={STATUS_TONE[current.status ?? ""] ?? "default"}>{t(`access.status.${current.status}`)}</Badge>{" "}
          {t(`access.kind.${current.kind}`)}
          {current.starts_on && current.ends_on ? ` · ${dateText(current.starts_on, i18n.language)} → ${dateText(current.ends_on, i18n.language)}` : ""}
          {current.invoice_number ? ` · ${t("access.students.invoice", { number: current.invoice_number })}` : ""}
          {current.note ? <span className="muted"> · {current.note}</span> : null}
        </p>
      ) : (
        <p className="muted">{t("access.students.noCurrent")}</p>
      )}
      <div className="row wrap">
        {canResend ? (
          <Button variant="secondary" disabled={resend.isPending} onClick={() => resend.mutate({ id })}>
            {t("access.students.resend")}
          </Button>
        ) : null}
        {canManage ? (
          <>
            <Button variant="secondary" onClick={startEditing}>
              {t("access.students.editExams")}
            </Button>
            <Button variant="secondary" onClick={() => setPending("extend")}>
              {t("access.students.extend")}
            </Button>
            {current?.status === "suspended" ? (
              <Button variant="secondary" onClick={() => resume.mutate({ id })}>
                {t("access.students.resume")}
              </Button>
            ) : current?.status === "active" ? (
              <Button variant="secondary" onClick={() => setPending("suspend")}>
                {t("access.students.suspend")}
              </Button>
            ) : null}
            {current ? (
              <Button variant="destructive" onClick={() => setPending("revoke")}>
                {t("access.students.revoke")}
              </Button>
            ) : null}
          </>
        ) : null}
      </div>
      {resend.isSuccess ? (
        <p role="status" className="ok">
          {t("access.students.resent")}
        </p>
      ) : null}
      <ErrorList error={error} />
      {editing ? (
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();
            changeExams.mutate({ id, data: { exams: examsPayload(choices) } }, { onSuccess: () => setEditing(false) });
          }}
        >
          <ExamPicker value={choices} onChange={setChoices} offered={EXAMS} />
          <div className="row">
            <Button type="submit" disabled={changeExams.isPending}>
              {t("access.students.saveExams")}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
              {t("common.cancel")}
            </Button>
          </div>
        </form>
      ) : null}
      <Dialog
        open={pending !== null}
        title={pending ? t(`access.students.confirm.${pending}`) : ""}
        onClose={() => setPending(null)}
        actions={
          <>
            <Button variant="ghost" onClick={() => setPending(null)}>
              {t("common.cancel")}
            </Button>
            <Button variant={pending === "revoke" ? "destructive" : "primary"} disabled={!note.trim() || (pending === "extend" && !Number(days))} onClick={confirm}>
              {t("access.students.confirmAction")}
            </Button>
          </>
        }
      >
        <div className="stack">
          {pending === "revoke" ? <p>{t("access.students.revokeHelp")}</p> : null}
          {pending === "extend" ? <TextField label={t("access.students.days")} type="number" min={1} value={days} onChange={(e) => setDays(e.target.value)} /> : null}
          <TextField label={t("access.students.note")} value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
      </Dialog>
      <OralCreditsAdmin learnerId={id} />
      <h3>{t("access.students.history")}</h3>
      {rows.length ? (
        <ul className="plain-list students__history">
          {rows.map((row) => (
            <li key={row.id}>
              <strong>{t(`access.history.${row.action.replace("academy.", "")}`, { defaultValue: row.action })}</strong>{" "}
              <span className="muted">
                {row.actor_name || t("access.students.system")} · {new Date(row.created_at).toLocaleString(i18n.language === "en" ? "en-GB" : "fr-FR", { timeZone: "Africa/Douala" })}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">{t("access.students.noHistory")}</p>
      )}
    </GlassCard>
  );
}

function ImportCsv({ columns }: { columns: readonly string[] }) {
  const { t } = useTranslation();
  const refresh = useRefreshAcademy();
  const [file, setFile] = useState<File | null>(null);
  const upload = useAcademyStudentsImportCreate({ mutation: { onSuccess: refresh } });
  const report: StudentImportRow[] = upload.data?.status === 200 ? upload.data.data : [];
  return (
    <GlassCard as="section" className="stack">
      <h2>{t("access.students.import")}</h2>
      <p className="muted">{t("access.students.importIntro")}</p>
      <code className="code-chip students__columns">{columns.join(";")}</code>
      <p className="muted">{t("access.students.importExample")}</p>
      <form
        className="row wrap"
        onSubmit={(event) => {
          event.preventDefault();
          if (file) upload.mutate({ data: { file } });
        }}
      >
        <label className="ao-field">
          <span className="ao-field__label">{t("access.students.csvFile")}</span>
          <input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </label>
        <Button type="submit" disabled={!file || upload.isPending}>
          {t("access.students.importRun")}
        </Button>
      </form>
      <ErrorList error={upload.error} />
      {report.length ? (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t("access.students.line")}</th>
                <th scope="col">{t("access.students.email")}</th>
                <th scope="col">{t("access.students.result")}</th>
              </tr>
            </thead>
            <tbody>
              {report.map((row) => (
                <tr key={row.line}>
                  <td>{row.line}</td>
                  <td>{row.email}</td>
                  <td>{row.status === "created" ? <Badge tone="success">{t("access.students.lineCreated")}</Badge> : <span className="ko">{row.message}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </GlassCard>
  );
}

type View = { kind: "list" } | { kind: "add" } | { kind: "import" } | { kind: "record"; id: string };

export function StudentsTab() {
  const { t, i18n } = useTranslation();
  const options = useAcademyStudentsOptionsRetrieve();
  const opts = options.data?.status === 200 ? options.data.data : undefined;
  const [view, setView] = useState<View>({ kind: "list" });
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [access, setAccess] = useState("");
  const list = useAcademyStudentsList({ page, ...(q ? { q } : {}), ...(access ? { access: access as "active" | "suspended" | "none" } : {}) });
  const data = list.data?.status === 200 ? list.data.data : undefined;
  const canManage = Boolean(opts?.can_manage);

  if (view.kind === "add") return <AddStudent onDone={(id) => setView({ kind: "record", id })} onOpen={(id) => setView({ kind: "record", id })} />;
  if (view.kind === "record")
    return <StudentRecord id={view.id} canManage={canManage} canResend={Boolean(opts?.can_resend)} onClose={() => setView({ kind: "list" })} />;

  return (
    <div className="stack">
      {view.kind === "import" ? <ImportCsv columns={opts?.csv_columns ?? []} /> : null}
      <GlassCard as="section" className="stack">
        <div className="section-title">
          <div>
            <h2>{t("access.students.title")}</h2>
            <p className="muted">{t("access.students.intro")}</p>
          </div>
          {canManage ? (
            <div className="row">
              <Button onClick={() => setView({ kind: "add" })}>{t("access.students.add")}</Button>
              <Button variant="secondary" onClick={() => setView(view.kind === "import" ? { kind: "list" } : { kind: "import" })}>
                {t("access.students.import")}
              </Button>
            </div>
          ) : null}
        </div>
        <div className="row wrap" role="search" aria-label={t("access.students.filters")}>
          <TextField label={t("access.students.search")} value={q} onChange={(e) => (setQ(e.target.value), setPage(1))} />
          <SelectField
            label={t("access.students.accessFilter")}
            value={access}
            onChange={(e) => (setAccess(e.target.value), setPage(1))}
            options={[
              { value: "", label: t("access.students.all") },
              { value: "active", label: t("access.status.active") },
              { value: "suspended", label: t("access.status.suspended") },
              { value: "none", label: t("access.students.noAccess") },
            ]}
          />
        </div>
        {data === undefined ? (
          <Skeleton style={{ height: 200 }} />
        ) : data.results.length === 0 ? (
          <p className="muted">{t("access.students.empty")}</p>
        ) : (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">{t("access.students.student")}</th>
                    <th scope="col">{t("access.students.exams")}</th>
                    <th scope="col">{t("access.students.accessColumn")}</th>
                    <th scope="col">
                      <span className="visually-hidden">{t("access.students.open")}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.results.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <strong>{row.full_name || row.email}</strong>
                        <div className="muted">{row.email}</div>
                      </td>
                      <td>
                        <div className="row wrap">
                          {EXAMS.filter((e) => row.access[e as keyof typeof row.access]).map((e) => (
                            <span key={e} className="code-chip">
                              {t(`access.examShort.${e}`)}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>
                        {row.current ? (
                          <Badge tone={STATUS_TONE[row.current.status ?? ""] ?? "default"}>
                            {t(`access.status.${row.current.status}`)}
                            {row.current.ends_on ? ` · ${dateText(row.current.ends_on, i18n.language)}` : ""}
                          </Badge>
                        ) : (
                          <Badge>{t("access.students.noAccess")}</Badge>
                        )}
                      </td>
                      <td>
                        <Button size="sm" variant="ghost" onClick={() => setView({ kind: "record", id: row.id })}>
                          {t("access.students.open")}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager page={page} hasNext={Boolean(data.next)} onChange={setPage} />
          </>
        )}
      </GlassCard>
    </div>
  );
}
